import React from 'react';

import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs from '~/timeInit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import { commercialHistorySeries } from './history';
import { money } from './review';
import type { CommercialRow, PerformanceDateRange } from './types';

export default function CommercialHistory({
  rows,
  date,
  range,
}: {
  rows: Array<CommercialRow>;
  date: string;
  range?: PerformanceDateRange;
}) {
  const endDate = range?.end_date ?? date;
  const startDate = range?.start_date ?? dayjs(endDate).subtract(29, 'day').format('YYYY-MM-DD');
  const days = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
  const selectedRows = rows.filter((row) => {
    const day = row.observation_date ?? row.report_date;
    return day >= startDate && day <= endDate;
  });
  const theme = useTheme();
  const series = commercialHistorySeries(selectedRows, endDate, days);
  const hasObservations = series.some((point) => point.LPC != null || point.LPP != null);
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Daily margin history
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AUD, after refunds · Attributed daily margin · Gaps mean no observations
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={2} sx={{ mt: 1.5 }}>
          {[
            { label: 'LPC', color: theme.palette.primary.main },
            { label: 'LPP', color: BRAND_COLORS.coralGreen },
          ].map(({ label, color }) => (
            <Stack key={label} direction="row" alignItems="center" gap={0.75}>
              <Box sx={{ width: 10, height: 10, bgcolor: color, border: 1, borderColor: 'divider' }} />
              <Typography variant="caption">{label}</Typography>
            </Stack>
          ))}
        </Stack>
        {hasObservations ? (
          <Box
            sx={{ height: 215, mt: 1 }}
            aria-label="Daily LPC and LPP margin chart. Missing observations are gaps; exact values are available in the table below."
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 12, right: 8, left: 4, bottom: 0 }} accessibilityLayer>
                <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => dayjs(value).format('D MMM')}
                  minTickGap={40}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                />
                <YAxis
                  width={62}
                  tickFormatter={(value) => money(value)}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                />
                <Tooltip
                  filterNull={false}
                  labelFormatter={(value) => dayjs(String(value)).format('D MMM YYYY')}
                  formatter={(value) => (value == null ? 'No observations' : money(Number(value)))}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                  itemStyle={{ color: theme.palette.text.primary }}
                />
                <ReferenceLine y={0} stroke={theme.palette.text.secondary} />
                <Bar dataKey="LPC" fill={theme.palette.primary.main} maxBarSize={18} isAnimationActive={false} />
                <Bar
                  dataKey="LPP"
                  fill={BRAND_COLORS.coralGreen}
                  stroke={theme.palette.primary.main}
                  strokeWidth={0.5}
                  maxBarSize={18}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No margin observations in the selected dates. Choose a wider range above to review more history.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {startDate} to {endDate} · Uses the same dates and attribution model as the performance totals.
        </Typography>
      </CardContent>
      <Accordion
        disableGutters
        elevation={0}
        sx={{ borderTop: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight={600}>
            View daily figures · selected dates
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer
            role="region"
            aria-label="Scrollable daily hotel figures"
            tabIndex={0}
            sx={{
              maxHeight: 340,
              overflow: 'auto',
              overscrollBehavior: 'contain',
              scrollbarGutter: 'stable',
              isolation: 'isolate',
            }}
          >
            <Table
              size="small"
              stickyHeader
              aria-label="Hotel commercial history"
              sx={{ minWidth: 640, '& td': { py: 1.5 } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">TTV after refunds</TableCell>
                  <TableCell align="right">Margin after refunds</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...selectedRows]
                  .sort((a, b) =>
                    (b.observation_date ?? b.report_date).localeCompare(a.observation_date ?? a.report_date),
                  )
                  .map((row) => (
                    <TableRow key={`${row.observation_date ?? row.report_date}-${row.product_bk}`}>
                      <TableCell>{row.observation_date ?? row.report_date}</TableCell>
                      <TableCell>{row.product_bk === 'tactical_ao_hotel' ? 'LPC' : 'LPP'}</TableCell>
                      <TableCell align="right">{money(row.ttv_observed_rows ? row.ttv_ar_aud : null)}</TableCell>
                      <TableCell align="right">{money(row.margin_observed_rows ? row.margin_ar_aud : null)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!selectedRows.length && (
            <Typography color="text.secondary">No commercial records in the selected dates.</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
}
